import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, User, LogOut, LayoutDashboard } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { useLogout } from "@/hooks/api/use-auth";
import { useSiteSettings } from "@/hooks/api/use-site-settings";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { getImageUrl } from "@/lib/image-utils";
import { useMemo } from "react";

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { t, isRTL } = useLanguage();
  const { isAuthenticated, user } = useAuth();
  const logoutMutation = useLogout();
  const { data: siteSettings, isLoading: isLoadingSettings } = useSiteSettings();

  // Use logo from API or fallback to local asset
  // Add timestamp for cache busting when logo changes
  const logoSrc = useMemo(() => {
    return getImageUrl(siteSettings?.data?.logo);
  }, [siteSettings?.data?.logo]);

  const navLinks = [
    { href: "/", label: t('nav.home') },
    { href: "/about", label: t('nav.about') },
    { href: "/how-it-works", label: t('nav.howItWorks') },
    { href: "/portfolio", label: t('nav.portfolio') },
    { href: "/contact", label: t('nav.contact') },
  ];

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-md border-b border-border">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3">
            {isLoadingSettings || !logoSrc ? (
              <div className="h-14 w-32 animate-pulse bg-gradient-to-r from-slate-200 via-slate-100 to-slate-200 bg-[length:200%_100%] rounded-lg" 
                   style={{ animation: 'shimmer 1.5s infinite' }} />
            ) : (
              <img src={logoSrc} alt="KYNZ Contracting" className="h-14 w-auto" />
            )}
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className={`text-sm font-medium transition-colors hover:text-primary ${
                  location.pathname === link.href
                    ? "text-primary"
                    : "text-foreground/70"
                }`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* Auth Buttons & Language Switcher */}
          <div className="hidden lg:flex items-center gap-4">
            <LanguageSwitcher />
            {isAuthenticated ? (
              <>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => {
                        const dashboardPath = user?.type === 'admin' 
                          ? '/admin/dashboard' 
                          : '/dashboard';
                        navigate(dashboardPath);
                      }}
                      className="relative"
                    >
                      <LayoutDashboard size={20} />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{t('cta.dashboard.button') || 'لوحة التحكم'}</p>
                  </TooltipContent>
                </Tooltip>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="relative">
                      <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center text-primary-foreground text-sm font-bold">
                        {user?.name?.charAt(0).toUpperCase() || 'U'}
                      </div>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align={isRTL ? "start" : "end"}>
                    <DropdownMenuItem onClick={() => {
                      const dashboardPath = user?.type === 'admin' 
                        ? '/admin/dashboard' 
                        : '/dashboard';
                      navigate(dashboardPath);
                    }}>
                      <LayoutDashboard className={`${isRTL ? 'ml-2' : 'mr-2'} h-4 w-4`} />
                      <span>{t('cta.dashboard.button') || 'لوحة التحكم'}</span>
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => {
                      const profilePath = user?.type === 'admin' 
                        ? '/admin/dashboard/profile' 
                        : '/dashboard/profile';
                      navigate(profilePath);
                    }}>
                      <User className={`${isRTL ? 'ml-2' : 'mr-2'} h-4 w-4`} />
                      <span>{t('nav.profile') || 'الملف الشخصي'}</span>
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      onClick={() => logoutMutation.mutate()}
                      className="text-destructive"
                    >
                      <LogOut className={`${isRTL ? 'ml-2' : 'mr-2'} h-4 w-4`} />
                      <span>{t('nav.logout') || 'تسجيل الخروج'}</span>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <>
                <Button variant="ghost" asChild>
                  <Link to="/auth">{t('nav.login')}</Link>
                </Button>
                <Button asChild>
                  <Link to="/auth?mode=register">{t('nav.register')}</Link>
                </Button>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <div className="flex items-center gap-2 lg:hidden">
            <LanguageSwitcher />
            <button
              className="p-2"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <div className="lg:hidden bg-background border-b border-border">
          <nav className="container mx-auto px-4 py-4 flex flex-col gap-4">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className={`text-sm font-medium transition-colors hover:text-primary ${
                  location.pathname === link.href
                    ? "text-primary"
                    : "text-foreground/70"
                }`}
                onClick={() => setIsMenuOpen(false)}
              >
                {link.label}
              </Link>
            ))}
            <div className="flex flex-col gap-2 pt-4 border-t border-border">
              {isAuthenticated ? (
                <>
                  <Button
                    variant="ghost"
                    onClick={() => {
                      const dashboardPath = user?.type === 'admin' 
                        ? '/admin/dashboard' 
                        : '/dashboard';
                      navigate(dashboardPath);
                      setIsMenuOpen(false);
                    }}
                  >
                    {t('cta.dashboard.button') || 'لوحة التحكم'}
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={() => {
                      const profilePath = user?.type === 'admin' 
                        ? '/admin/dashboard/profile' 
                        : '/dashboard/profile';
                      navigate(profilePath);
                      setIsMenuOpen(false);
                    }}
                  >
                    {t('nav.profile') || 'الملف الشخصي'}
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={() => {
                      logoutMutation.mutate();
                      setIsMenuOpen(false);
                    }}
                    className="text-destructive"
                  >
                    {t('nav.logout') || 'تسجيل الخروج'}
                  </Button>
                </>
              ) : (
                <>
                  <Button variant="ghost" asChild>
                    <Link to="/auth" onClick={() => setIsMenuOpen(false)}>{t('nav.login')}</Link>
                  </Button>
                  <Button asChild>
                    <Link to="/auth?mode=register" onClick={() => setIsMenuOpen(false)}>{t('nav.register')}</Link>
                  </Button>
                </>
              )}
            </div>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
